import React from 'react';
import { Flex, Typography } from 'antd';
import { Doughnut } from 'react-chartjs-2';
import { EyeInvisibleOutlined } from '@ant-design/icons';

import { ColorsList, percentageFormatter } from '../Chart';
import { crawlerCategoryLabel } from '~features/crawler-filter';
import { LegendLabelSquare } from '~components/Charts';
import TOOLTIPS from './tooltips';
import { type ChartItemsVisibilityProps } from '~library/chartjs';
import { getPercentage } from '~library/numbers';
import { useIsPrinting } from '~library/print';

export const SIDE_CHART_ID = 'pages-crawled-side-chart';

type DoughnutChartProps = {
  totalRequestPerCrawler: [string, number][];
} & ChartItemsVisibilityProps;

function DoughnutChart({ totalRequestPerCrawler, itemsVisibility, onItemClicked }: DoughnutChartProps) {
  const { isPrinting } = useIsPrinting();
  const totalVisibleRequests = React.useMemo(() => {
    return totalRequestPerCrawler.reduce((acc, [_, value], i) => {
      if (itemsVisibility[i]) {
        acc += value;
      }
      return acc;
    }, 0);
  }, [totalRequestPerCrawler, itemsVisibility]);

  const crawlerPercentagesData = React.useMemo(() => {
    const data = totalRequestPerCrawler.reduce<{
      labels: string[];
      datasets: {
        data: number[];
        backgroundColor: string[];
      }[];
    }>(
      (acc, [name, value], i) => {
        acc.labels.push(crawlerCategoryLabel(name));
        acc.datasets[0].data.push(getPercentage(value, totalVisibleRequests));
        acc.datasets[0].backgroundColor.push(ColorsList[i]);
        return acc;
      },
      {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: [],
          },
        ],
      }
    );
    return data;
  }, [totalRequestPerCrawler, totalVisibleRequests]);

  return (
    <Flex justify="space-between" vertical style={{ height: '100%' }}>
      <div>
        {totalRequestPerCrawler.map(([crawler], i) => (
          <Flex justify="space-between" key={crawler}>
            <LegendLabelSquare
              color={ColorsList[i]}
              small
              label={crawlerCategoryLabel(crawler)}
              tooltipContent={TOOLTIPS[crawler]}
              crossedOut={!itemsVisibility[i]}
              onClick={() => onItemClicked(i)}
            />
            {itemsVisibility[i] ? (
              <Typography.Text>{percentageFormatter(crawlerPercentagesData.datasets[0].data[i])}</Typography.Text>
            ) : (
              <EyeInvisibleOutlined />
            )}
          </Flex>
        ))}
      </div>

      <div style={{ height: 156, minWidth: 156 }}>
        <Doughnut
          data={crawlerPercentagesData}
          id={SIDE_CHART_ID}
          options={{
            animation: isPrinting ? false : undefined,
            plugins: {
              tooltip: {
                callbacks: {
                  label: (context) => percentageFormatter(context.parsed),
                },
              },
            },
          }}
        />
      </div>
    </Flex>
  );
}

export default DoughnutChart;
